import { buildPrompt } from '@/ai/utils/promptBuilder'
import { transformWorkflow } from '@/ai/utils/transformWorkflow'
import { readModelNotes } from '@/ai/prompts/common/instructions'
import { systemMessages } from '@/ai/prompts/common/systemMessages'
import { examples, getEventExample } from '@/ai/prompts/common/examples'
import { getJsonArrayOutputFormatting } from '@/ai/prompts/common/formatting'
import { createSystemMessage, createUserMessage } from '@/ai/utils/promptUtils'

export const buildWorkflowReadModelsPrompt = ({ workflow, cardTypes }) => {
  const messages = []

  const systemContent = buildPrompt([systemMessages.EVENT_MODELING_DDD_EXPERT])
  messages.push(createSystemMessage(systemContent))

  const promptSections = []

  promptSections.push({
    title: 'Request',
    content: `
      For each domain event, generate a Read Model (or Query Model) for the actor (human or system) about to make the
      necessary decisions to take action and complete the step. The purpose of the Read Model (or Query Model)
      is to define a query (for one or many items) that displays the current system state in a way that is relevant
      to the actor BEFORE doing the task at hand. Provide the Read Model as a set of data fields to be displayed
      to the actor BEFORE invoking the current step command the aggregate root. Usually, between 2 and 8 fields are
      sufficient, but it can be more or less. In most of the cases the generated data fields should match with the
      data fields of the entity and include the primary key field (id) of the entity. Tag each data field with INDEXED
      if it should be indexed for filtering or search. An attribute can be a primitive or a reference to a
      non-primitive type like an associated entity. An attribute or field name must be in camel case format
      (e.g., 'productName' instead of 'Product Name'). An attribute or field name must be at most 30 characters long.
      For each Read Model, provide the name of the entity that is queried to build it. If the entity exists in
      the Existing Entities list, use the exact same name. If it does not exist, provide a new entity name
      in human-readable format (not camel case).
    `
  })

  promptSections.push({
    title: 'Workflow Description',
    content: `\`${workflow.aiDescription}\`` || 'N/A'
  })

  const { events, entities, eventIdToLongIdMap } = transformWorkflow(workflow)

  promptSections.push({
    title: 'Current Workflow (JSON format)',
    contentType: 'codeblock',
    language: 'json',
    content: JSON.stringify({ domainEvents: events })
  })

  promptSections.push({
    title: 'Existing Entities (Optional): Use this information if applicable.',
    contentType: 'codeblock',
    language: 'json',
    content: JSON.stringify({ entities })
  })

  promptSections.push({
    title: 'Very important considerations',
    contentType: 'bullet',
    content: readModelNotes
  })

  const formattingContent = `The resulting array must contain ${workflow.eventsJson.length} items.`

  const options = {
    shouldAddCards: false,
    shouldAddDataModels: true
  }

  const exampleData1 = {
    id: 1,
    readModelExample: examples.readModelExampleOrder,
    queriedEntity: 'Order'
  }
  const exampleData2 = {
    id: 2,
    readModelExample: examples.readModelExampleCustomer,
    queriedEntity: 'Customer'
  }

  const eventsExample = [
    getEventExample(exampleData1, cardTypes, options),
    getEventExample(exampleData2, cardTypes, options)
  ]

  promptSections.push({
    title: 'Formatting',
    flattenLines: false,
    content: getJsonArrayOutputFormatting(JSON.stringify(eventsExample), formattingContent)
  })

  const userContent = buildPrompt(promptSections)
  messages.push(createUserMessage(userContent))

  return { messages, eventIdToLongIdMap }
}
